import React from "react";
import styled from "styled-components";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import gfm from "remark-gfm";

const CreditsContainer = styled.div`
  margin: 1rem 0;
  color: #888;
  max-width: 800px;
`;

const CreditsTitle = styled.div`
  font-weight: bold;
  margin-bottom: 0.5rem;
`;

const MarkdownCredits = styled.div`
  line-height: 1.5;
  font-size: 12px;

  p {
    margin: 0.5rem 0;
  }

  ul,
  ol {
    margin: 0.5rem 0;
    padding-left: 1.5rem;
  }

  li {
    margin: 0.25rem 0;
  }

  strong {
    font-weight: bold;
  }
`;

const ArrayCreditsList = styled.ul`
  margin: 0;
  padding-left: 1.5rem;
`;

const ArrayCreditsItem = styled.li`
  margin: 0.25rem 0;
`;

/**
 * Credits component that supports both markdown string and array format
 * @param {Object} props
 * @param {string|Array} props.credits - Either a markdown string or an array of {label, content} objects
 * @param {string} props.title - Optional title for the credits section (default: "Credits")
 */
const Credits = ({ credits, title = "Credits" }) => {
  if (!credits) {
    return null;
  }

  // Handle markdown string format
  if (typeof credits === "string") {
    return (
      <CreditsContainer>
        <CreditsTitle>{title}</CreditsTitle>
        <MarkdownCredits>
          <ReactMarkdown remarkPlugins={[gfm]} rehypePlugins={[rehypeRaw]}>
            {credits}
          </ReactMarkdown>
        </MarkdownCredits>
      </CreditsContainer>
    );
  }

  // Handle array format (same as song credits)
  if (Array.isArray(credits) && credits.length > 0) {
    // Separate credits into "other" credits (production, mixing, etc.) and music credits
    const otherCredits =
      credits.filter((c) =>
        [
          "cover art",
          "photography",
          "mixed",
          "mixer",
          "mixing",
          "mastered",
          "mastering",
          "master",
          "arranged",
          "arranger",
          "produced",
          "producer",
          "recorded",
          "recording",
          "recorder",
          "production",
          "publisher",
        ].some((prefix) => c.label.toLowerCase().startsWith(prefix))
      ) ?? [];
    const musicCredits =
      credits.filter(
        (c) => !otherCredits.map((oc) => oc.label).includes(c.label)
      ) ?? [];

    return (
      <CreditsContainer>
        {musicCredits.length > 0 && (
          <>
            <CreditsTitle>{title}</CreditsTitle>
            <ArrayCreditsList>
              {musicCredits.map((c) => (
                <ArrayCreditsItem key={c.label + c.content}>
                  <strong>{c.content}:</strong> {c.label}
                </ArrayCreditsItem>
              ))}
            </ArrayCreditsList>
          </>
        )}
        {otherCredits.length > 0 && (
          <ArrayCreditsList>
            {otherCredits.map((c) => (
              <ArrayCreditsItem key={c.label + c.content}>
                <strong>{c.label}:</strong> {c.content}
              </ArrayCreditsItem>
            ))}
          </ArrayCreditsList>
        )}
      </CreditsContainer>
    );
  }

  return null;
};

export default Credits;
